import React, { useState, useEffect } from 'react';
import { useBusiness, BusinessContext, Business } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { Resource } from "../lib/resources";
import { Clock, PlayCircle, StopCircle, PauseCircle, Timer, ArrowRight, AlertCircle, ChevronDown, MoreHorizontal, Pencil, X, Trash2, Plus, User, Calendar, CheckCircle2, Check, Filter, Clock4, ChevronRight, Search } from 'lucide-react';
import ManualEntryModal from '../components/timeclock/ManualEntryModal';
import ActiveTimerWarningModal from '../components/ActiveTimerWarningModal';
import { format, parseISO, startOfMonth, endOfMonth, startOfDay, endOfDay, subMonths, subDays, subWeeks, addDays } from "date-fns";
import { toZonedTime } from 'date-fns-tz';
import PermissionGuard from '../components/PermissionGuard';



interface TimelineEvent {
  type: 'clock_in' | 'break_start' | 'break_end' | 'clock_out';
  time: Date;
}

interface PayrollEntry {
  id: string;
  resources: Resource;
  business_id: string;
  resource_id: string;
  rate_amount: number;
  status: string;
  compensation_type: string;
  clock_in: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes: number;
  entry_type: string;
  flat_rate?: boolean;
}

interface PayrollHistoryEntry {
  id: string;
  business_id: string;
  resource_id: string;
  clock_in: string;
  clock_out: string | null;
  break_start: string | null;
  break_end: string | null;
  total_minutes: number;
  total_hours: number;
  rate_amount: number;
  total_earnings: number;
  status: string;
  compensation_type: string;
  entry_type: string;
  created_at: string;
  flat_rate?: boolean;
  resource_name?: string;
  resources: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

interface OnTheClockEntry {
  id: string;
  resource_id: string;
  clock_in: string;
  rate_amount: number;
  resources: {
    first_name: string;
    last_name: string;
  };
  clockInTime: Date;
}

// Date range options
const DATE_RANGES = [
  { 
    label: 'This Month', 
    value: 'this_month',
    getRange: () => ({
      start: startOfMonth(new Date()),
      end: endOfMonth(new Date())
    })
  },
  { 
    label: 'Last Month', 
    value: 'last_month',
    getRange: () => {
      const lastMonth = subMonths(new Date(), 1);
      return {
        start: startOfMonth(lastMonth),
        end: endOfMonth(lastMonth)
      };
    }
  },
  { 
    label: 'This Week', 
    value: 'this_week',
    getRange: () => {
      const today = new Date();
      const monday = subDays(today, today.getDay() - 1);
      const sunday = addDays(monday, 6);
      return {
        start: startOfDay(monday),
        end: endOfDay(sunday)
      };
    }
  },
  { 
    label: 'Last Week', 
    value: 'last_week',
    getRange: () => {
      const lastWeek = subWeeks(new Date(), 1);
      const monday = subDays(lastWeek, lastWeek.getDay() - 1);
      const sunday = addDays(monday, 6);
      return {
        start: startOfDay(monday),
        end: endOfDay(sunday)
      };
    }
  },
  { 
    label: 'Custom Range', 
    value: 'custom',
    getRange: (startDate?: Date, endDate?: Date) => ({
      start: startDate ? startOfDay(startDate) : new Date(),
      end: endDate ? endOfDay(endDate) : new Date()
    })
  }
];

export default function Timeclock() {
  const { selectedBusiness } = useBusiness();
  const [currentResource, setCurrentResource] = useState<Resource | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [clockedIn, setClockedIn] = useState(false);
  const [onBreak, setOnBreak] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [breakStartTime, setBreakStartTime] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [currentPayrollEntryId, setCurrentPayrollEntryId] = useState<string | null>(null);
  const [payrollHistory, setPayrollHistory] = useState<PayrollHistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<PayrollHistoryEntry | null>(null);
  const [editFormData, setEditFormData] = useState({
    clockIn: '',
    clockOut: '',
    breakStart: '',
    breakEnd: '',
    rate: 0
  });

  // Add state for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<PayrollHistoryEntry | null>(null);

  // Add state for manual entry modal
  const [isManualEntryModalOpen, setIsManualEntryModalOpen] = useState(false);
  
  // Add state for active timer warning modal
  const [isActiveTimerWarningOpen, setIsActiveTimerWarningOpen] = useState(false);
  const [activeTimerBookingTitle, setActiveTimerBookingTitle] = useState<string>('');

  const [onTheClockEntries, setOnTheClockEntries] = useState<OnTheClockEntry[]>([]);

  // Add new state for live calculations
  const [liveElapsedTimes, setLiveElapsedTimes] = useState<{[key: string]: string}>({});
  const [liveEarnings, setLiveEarnings] = useState<{[key: string]: number}>({});

  // Add new state for date range filtering
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState(DATE_RANGES[0]);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isCustomRangeOpen, setIsCustomRangeOpen] = useState(false);
  const datePickerRef = React.useRef<HTMLDivElement>(null);

  // Timeclock permissions removed - always accessible

  useEffect(() => {
    if (selectedBusiness?.id) {
      loadCurrentResource();
      loadResources();
      loadPayrollHistory();
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (selectedResource?.id && selectedBusiness?.id) {
      loadPayrollHistory();
    }
  }, [selectedResource?.id, selectedBusiness?.id]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    
    if (clockedIn && startTime) {
      intervalId = setInterval(() => {
        const now = new Date();
        let diff = now.getTime() - startTime.getTime();
        
        // Subtract break time if on break
        if (onBreak && breakStartTime) {
          diff = diff - (now.getTime() - breakStartTime.getTime());
        }
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        setElapsedTime(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [clockedIn, startTime, onBreak, breakStartTime]);

  const loadResources = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'active')
        .order('first_name');

      if (error) throw error;
      setResources(data || []);
    } catch (error) {
      console.error('Error loading resources:', error);
    }
  };

  const loadCurrentResource = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      if (!user) {
        console.error('No user found');
        setIsLoading(false);
        return;
      }

      // Check if user is admin first
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('business_id', selectedBusiness.id);

      if (userRoles && userRoles.some(ur => ur.role === 'admin')) {
        setIsAdmin(true);
        setIsLoading(false);
        return;
      }

      // If not admin, try to get resource
      const { data: resource, error: resourceError } = await supabase
        .from('resources')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('auth_id', user.id)
        .eq('status', 'active')
        .single();

      if (!resourceError) {
        setCurrentResource(resource);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading current resource:', error);
      setIsLoading(false);
    }
  };

  const loadPayrollHistory = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    setIsLoadingHistory(true);
    
    try {
      let dateRange;
      if (selectedRange.value === 'custom' && customStartDate && customEndDate) {
        dateRange = selectedRange.getRange(new Date(customStartDate), new Date(customEndDate));
      } else {
        dateRange = selectedRange.getRange();
      }

      // Convert dates to UTC for database query
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const utcStartDate = toZonedTime(dateRange.start, 'UTC');
      const utcEndDate = toZonedTime(dateRange.end, 'UTC');

      let query = supabase
        .from('payroll_page')
        .select(`
          *,
          resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('entry_type', 'timeclock')
        .gte('clock_in', utcStartDate.toISOString())
        .lte('clock_in', utcEndDate.toISOString())
        .order('clock_in', { ascending: false });

      if (selectedResource?.id) {
        query = query.eq('resource_id', selectedResource.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert UTC dates back to local timezone for display
      const historyWithLocalDates = data.map((entry: any) => ({
        ...entry,
        clock_in: entry.clock_in ? toZonedTime(new Date(entry.clock_in), timezone).toISOString() : null,
        clock_out: entry.clock_out ? toZonedTime(new Date(entry.clock_out), timezone).toISOString() : null,
        break_start: entry.break_start ? toZonedTime(new Date(entry.break_start), timezone).toISOString() : null,
        break_end: entry.break_end ? toZonedTime(new Date(entry.break_end), timezone).toISOString() : null,
        resource_name: entry.resources ? `${entry.resources.first_name} ${entry.resources.last_name}` : 'Unknown',
        total_earnings: calculateEarnings(entry)
      }));

      setPayrollHistory(historyWithLocalDates);
      setError(null);
    } catch (err) {
      console.error('Error loading payroll history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load payroll history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadOnTheClockEntries = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      let query = supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          clock_in,
          rate_amount,
          resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('entry_type', 'timeclock')
        .is('clock_out', null)
        .order('clock_in', { ascending: false });

      if (selectedResource?.id) {
        query = query.eq('resource_id', selectedResource.id);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Store the entries with their clock in times
      const entries = (data || []).map((entry: any) => ({
        id: entry.id,
        resource_id: entry.resource_id,
        clock_in: entry.clock_in,
        rate_amount: entry.rate_amount,
        resources: {
          first_name: entry.resources.first_name,
          last_name: entry.resources.last_name
        },
        clockInTime: new Date(entry.clock_in)
      }));
      
      setOnTheClockEntries(entries);
    } catch (error) {
      console.error('Error loading on the clock entries:', error);
    }
  };

  // Add new effect for live updates
  useEffect(() => {
    // Update elapsed times and earnings every second
    const updateLiveTimes = () => {
      const now = new Date();
      const newElapsedTimes: {[key: string]: string} = {};
      const newEarnings: {[key: string]: number} = {};
      
      onTheClockEntries.forEach(entry => {
        const elapsedMs = now.getTime() - entry.clockInTime.getTime();
        const elapsedMinutes = Math.floor(elapsedMs / (1000 * 60));
        const elapsedHours = elapsedMs / (1000 * 60 * 60);
        
        newElapsedTimes[entry.id] = formatDuration(elapsedMinutes);
        newEarnings[entry.id] = elapsedHours * entry.rate_amount;
      });
      
      setLiveElapsedTimes(newElapsedTimes);
      setLiveEarnings(newEarnings);
    };

    // Initial update
    updateLiveTimes();
    
    // Update every second
    const intervalId = setInterval(updateLiveTimes, 1000);
    
    return () => clearInterval(intervalId);
  }, [onTheClockEntries]);

  // Modify the useEffect for loading entries to run every 30 seconds
  useEffect(() => {
    loadOnTheClockEntries();
    const intervalId = setInterval(loadOnTheClockEntries, 30000); // Refresh from DB every 30 seconds
    
    return () => clearInterval(intervalId);
  }, [selectedBusiness?.id, selectedResource?.id]);

  const handleResourceSelect = (resource: Resource) => {
    setSelectedResource(resource);
    // Reset all states when changing resource
    setClockedIn(false);
    setOnBreak(false);
    setStartTime(null);
    setBreakStartTime(null);
    setElapsedTime('00:00:00');
    setTimelineEvents([]);
    setCurrentPayrollEntryId(null);
  };

  const createPayrollEntry = async (entry: PayrollEntry) => {
    if (!supabase) return;

    try {
      const { data, error } = await supabase
        .from('payroll_page')
        .insert({
          ...entry,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          status: 'active',
          entry_type: 'time',
          compensation_type: 'hourly',
          total_minutes: 0
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating payroll entry:', error);
      throw error;
    }
  };

  const updatePayrollEntry = async (id: string, updates: Partial<PayrollEntry>) => {
    if (!supabase) return;

    try {
      const { data, error } = await supabase
        .from('payroll_page')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating payroll entry:', error);
      throw error;
    }
  };

  const handleClockIn = async () => {
    if (!selectedResource || !selectedBusiness?.id || !supabase) return;
    
    const now = new Date();
    
    try {
      // Get resource's details for later use
      const { data: resourceData, error: resourceError } = await supabase
        .from('resources')
        .select('hourly_rate, compensation_type')
        .eq('id', selectedResource.id)
        .single();

      if (resourceError) throw resourceError;
      if (!resourceData) throw new Error('Resource not found');
      
      // Create a new entry directly in the payroll_page table
      const entry = {
        business_id: selectedBusiness.id,
        resource_id: selectedResource.id,
        clock_in: now.toISOString(),
        entry_type: 'timeclock',
        status: 'active',
        process_status: 'UNPAID',
        compensation_type: resourceData.compensation_type || 'hourly',
        rate_amount: resourceData.hourly_rate || 0,
        total_hours: 0,
        total_minutes: 0,
        total_earnings: 0,
        created_at: now.toISOString(),
        updated_at: now.toISOString()
      };

      console.log('Creating payroll entry for clock in:', entry);

      const { data, error } = await supabase
        .from('payroll_page')
        .insert(entry)
        .select()
        .single();

      if (error) {
        console.error('Error creating payroll entry:', error);
        throw error;
      }

      console.log('Created payroll entry:', data);
      setCurrentPayrollEntryId(data.id);
      
      setClockedIn(true);
      setStartTime(now);
      setElapsedTime('00:00:00');
      setTimelineEvents([{ type: 'clock_in', time: now }]);
    } catch (error) {
      console.error('Error during clock in:', error);
      alert('Failed to clock in. Please try again.');
    }
  };

  const handleClockOut = async () => {
    if (!selectedResource || !selectedBusiness?.id || !supabase || !startTime || !currentPayrollEntryId) return;

    // Check for active timers before allowing clock out
    try {
      const { data: activeTimerLogs, error: timerError } = await supabase
        .from('booking_timer_logs')
        .select(`
          booking_id,
          bookings!inner(title)
        `)
        .eq('resource_id', selectedResource.id)
        .eq('is_running', true)
        .limit(1);

      if (timerError) {
        console.error('Error checking for active timers:', timerError);
      } else if (activeTimerLogs && activeTimerLogs.length > 0) {
        // Active timer found, show warning modal
        const bookingTitle = activeTimerLogs[0].bookings?.title || 'Unknown Booking';
        setActiveTimerBookingTitle(bookingTitle);
        setIsActiveTimerWarningOpen(true);
        return; // Don't proceed with clock out
      }
    } catch (error) {
      console.error('Error checking for active timers:', error);
    }

    const now = new Date();
    try {
      // Calculate total minutes worked
      let totalMs = now.getTime() - startTime.getTime();
      
      // Subtract break time if there was a break
      if (breakStartTime && !onBreak) {
        const breakEndTime = timelineEvents.find(e => e.type === 'break_end')?.time;
        if (breakEndTime) {
          const breakMs = breakEndTime.getTime() - breakStartTime.getTime();
          totalMs -= breakMs;
        }
      }
      
      const totalMinutes = Math.floor(totalMs / (1000 * 60));
      
      // Get resource's hourly rate
      const { data: resourceData, error: resourceError } = await supabase
        .from('resources')
        .select('hourly_rate, compensation_type')
        .eq('id', selectedResource.id)
        .single();

      if (resourceError) throw resourceError;
      if (!resourceData) throw new Error('Resource not found');

      // Calculate hours and earnings
      const hours = totalMinutes / 60;
      const earnings = hours * (resourceData.hourly_rate || 0);

      // Update the existing payroll entry
      const updates = {
        clock_out: now.toISOString(),
        total_minutes: totalMinutes,
        total_hours: hours,
        total_earnings: earnings,
        updated_at: now.toISOString()
      };

      console.log('Updating payroll entry for clock out:', updates);

      const { data, error } = await supabase
        .from('payroll_page')
        .update(updates)
        .eq('id', currentPayrollEntryId)
        .select();

      if (error) {
        console.error('Error updating payroll entry:', error);
        throw error;
      }

      console.log('Updated payroll entry:', data);
      
      setClockedIn(false);
      setStartTime(null);
      setOnBreak(false);
      setBreakStartTime(null);
      setCurrentPayrollEntryId(null);
      setTimelineEvents([...timelineEvents, { type: 'clock_out', time: now }]);
      
      // Reload payroll history after clocking out
      loadPayrollHistory();
    } catch (error) {
      console.error('Error during clock out:', error);
      alert('Failed to clock out. Please try again.');
    }
  };

  const handleBreak = async () => {
    if (!selectedResource || !currentPayrollEntryId || !supabase) return;

    const now = new Date();
    try {
      if (!onBreak) {
        // Starting break
        const { error } = await supabase
          .from('payroll_page')
          .update({
            break_start: now.toISOString(),
            updated_at: now.toISOString()
          })
          .eq('id', currentPayrollEntryId);

        if (error) {
          console.error('Error updating break start:', error);
          throw error;
        }
        
        setOnBreak(true);
        setBreakStartTime(now);
        setTimelineEvents([...timelineEvents, { type: 'break_start', time: now }]);
      } else {
        // Ending break
        const { error } = await supabase
          .from('payroll_page')
          .update({
            break_end: now.toISOString(),
            updated_at: now.toISOString()
          })
          .eq('id', currentPayrollEntryId);

        if (error) {
          console.error('Error updating break end:', error);
          throw error;
        }
        
        setOnBreak(false);
        setTimelineEvents([...timelineEvents, { type: 'break_end', time: now }]);
      }
    } catch (error) {
      console.error('Error handling break:', error);
      alert('Failed to update break status. Please try again.');
    }
  };

  const getEventLabel = (type: TimelineEvent['type']) => {
    switch (type) {
      case 'clock_in':
        return 'Clocked In';
      case 'break_start':
        return 'Break Started';
      case 'break_end':
        return 'Break Ended';
      case 'clock_out':
        return 'Clocked Out';
    }
  };

  const getEventColor = (type: TimelineEvent['type']) => {
    switch (type) {
      case 'clock_in':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'break_start':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'break_end':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'clock_out':
        return 'bg-red-100 text-red-800 border-red-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };
  
  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  
  const formatTime = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    // Use toLocaleTimeString with the user's timezone to display time correctly
    return date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    });
  };
  
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD'
    }).format(amount);
  };

  const activeResource = isAdmin ? selectedResource : currentResource;

  // Handle opening the edit modal
  const handleEditClick = (entry: PayrollHistoryEntry) => {
    setEditingEntry(entry);
    // Convert UTC timestamps to local timezone for the form inputs
    const clockIn = entry.clock_in ? new Date(entry.clock_in) : null;
    const clockOut = entry.clock_out ? new Date(entry.clock_out) : null;
    const breakStart = entry.break_start ? new Date(entry.break_start) : null;
    const breakEnd = entry.break_end ? new Date(entry.break_end) : null;
    
    setEditFormData({
      clockIn: clockIn ? new Date(clockIn.getTime() - clockIn.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '',
      clockOut: clockOut ? new Date(clockOut.getTime() - clockOut.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '',
      breakStart: breakStart ? new Date(breakStart.getTime() - breakStart.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '',
      breakEnd: breakEnd ? new Date(breakEnd.getTime() - breakEnd.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '',
      rate: entry.rate_amount
    });
    setIsEditModalOpen(true);
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  // Save the edited entry
  const handleSaveEdit = async () => {
    if (!editingEntry || !supabase) return;

    try {
      // Convert form string datetime to ISO strings, adjusting for timezone
      const convertToUTC = (dateTimeString: string) => {
        if (!dateTimeString) return null;
        // Create date in local timezone
        const localDate = new Date(dateTimeString);
        // Convert to UTC string
        return localDate.toISOString();
      };
      
      const clockIn = convertToUTC(editFormData.clockIn);
      const clockOut = convertToUTC(editFormData.clockOut);
      const breakStart = convertToUTC(editFormData.breakStart);
      const breakEnd = convertToUTC(editFormData.breakEnd);
      
      // Calculate total minutes if both clock in and out are provided
      let totalMinutes = 0;
      let totalHours = 0;
      let totalEarnings = 0;
      
      if (clockIn && clockOut) {
        let totalMs = new Date(clockOut).getTime() - new Date(clockIn).getTime();
        
        // Subtract break time if there was a break
        if (breakStart && breakEnd) {
          const breakMs = new Date(breakEnd).getTime() - new Date(breakStart).getTime();
          totalMs -= breakMs;
        }
        
        totalMinutes = Math.floor(totalMs / (1000 * 60));
        totalHours = totalMinutes / 60;
        totalEarnings = totalHours * editFormData.rate;
      }

      // Update the entry
      const updates = {
        clock_in: clockIn,
        clock_out: clockOut,
        break_start: breakStart,
        break_end: breakEnd,
        total_minutes: totalMinutes,
        total_hours: totalHours,
        rate_amount: editFormData.rate,
        total_earnings: totalEarnings,
        updated_at: new Date().toISOString()
      };

      console.log('Updating payroll entry:', updates);

      const { data, error } = await supabase
        .from('payroll_page')
        .update(updates)
        .eq('id', editingEntry.id)
        .select();

      if (error) {
        console.error('Error updating payroll entry:', error);
        throw error;
      }

      console.log('Updated payroll entry:', data);
      
      // Close modal and refresh data
      setIsEditModalOpen(false);
      setEditingEntry(null);
      loadPayrollHistory();
    } catch (error) {
      console.error('Error saving edits:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  // Handle opening the delete confirmation modal
  const handleDeleteClick = (entry: PayrollHistoryEntry) => {
    setEntryToDelete(entry);
    setIsDeleteModalOpen(true);
  };

  // Delete the selected entry
  const handleDeleteConfirm = async () => {
    if (!entryToDelete || !supabase) return;

    try {
      const { error } = await supabase
        .from('payroll_page')
        .delete()
        .eq('id', entryToDelete.id);

      if (error) {
        console.error('Error deleting payroll entry:', error);
        throw error;
      }

      console.log('Deleted payroll entry:', entryToDelete.id);
      
      // Close modal and refresh data
      setIsDeleteModalOpen(false);
      setEntryToDelete(null);
      loadPayrollHistory();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Failed to delete entry. Please try again.');
    }
  };

  // Add click outside handler for date picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsCustomRangeOpen(false);
        setIsDateRangeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add useEffect to reload history when date range changes
  useEffect(() => {
    loadPayrollHistory();
  }, [selectedRange, customStartDate, customEndDate, selectedBusiness?.id, selectedResource?.id]);

  // Calculate earnings for a time entry
  const calculateEarnings = (entry: any) => {
    if (!entry.rate_amount || !entry.total_minutes) return 0;
    return (entry.rate_amount / 60) * entry.total_minutes;
  };

  // Force clock out a specific resource
  const handleForceClockOut = async (entryId: string, resourceId: string, clockInTime: string, rateAmount: number) => {
    if (!selectedBusiness?.id || !supabase) return;

    const now = new Date();
    try {
      // Calculate total minutes worked
      const clockIn = new Date(clockInTime);
      const totalMs = now.getTime() - clockIn.getTime();
      const totalMinutes = Math.floor(totalMs / (1000 * 60));
      
      // Calculate hours and earnings
      const hours = totalMinutes / 60;
      const earnings = hours * rateAmount;

      // Update the payroll entry to clock out
      const updates = {
        clock_out: now.toISOString(),
        total_minutes: totalMinutes,
        total_hours: hours,
        total_earnings: earnings,
        updated_at: now.toISOString()
      };

      console.log('Force clocking out entry:', entryId, updates);

      const { data, error } = await supabase
        .from('payroll_page')
        .update(updates)
        .eq('id', entryId)
        .select();

      if (error) {
        console.error('Error force clocking out:', error);
        throw error;
      }

      console.log('Force clock out successful:', data);
      
      // Reload data to refresh the on-the-clock entries
      loadOnTheClockEntries();
      loadPayrollHistory();
    } catch (error) {
      console.error('Error during force clock out:', error);
      alert('Failed to force clock out. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="bg-gray-100 flex items-center justify-center" style={{ 
        height: 'calc(100vh - 3rem)',
        maxHeight: 'calc(100vh - 3rem)',
        minHeight: 0
      }}>
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
              <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Timeclock</h3>
          <p className="text-gray-600">Setting up time tracking...</p>
        </div>
      </div>
    );
  }

  // Check timeclock permissions
  return (
    <PermissionGuard permission="timeclock.pageAccess">
      <style jsx global>{`
        /* Hide scrollbar for timeclock history */
        .timeclock-scroll-container::-webkit-scrollbar {
          display: none;
        }
        
        .timeclock-scroll-container {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      <div className="bg-gray-100" data-timeclock-page style={{ 
        height: 'calc(100vh - 3rem)',
        maxHeight: 'calc(100vh - 3rem)',
        minHeight: 0,
        overflow: 'auto'
      }}>
        <div className="px-0 py-4">
          <div className="space-y-8">
            {/* Main Content Grid */}
            <div className="grid grid-cols-1 gap-6">
              {/* Temporarily Hidden - On The Clock Section */}
              {/* <div className="lg:col-span-4 space-y-6">
                ... On The Clock content ...
              </div> */}

              {/* Full Width - History Section */}
              <div className="col-span-1 mx-2 sm:mx-0">
                <div className="bg-gray-100 rounded-none sm:rounded-xl shadow-sm border-0 sm:border border-gray-200 overflow-hidden">
                   {/* History Header */}
                   <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-gray-200 bg-white">
                     <div className="flex flex-col gap-3">
                       <div className="flex items-center gap-3">
                         <div>
                           <h3 className="text-lg font-semibold text-gray-900">Time Clock History</h3>
                           <p className="text-sm text-gray-600 hidden sm:block">Past time entries and records</p>
                         </div>
                       </div>
                       
                       <div className="flex items-center gap-2">
                         {/* Resource Selector */}
                         <div className="relative flex-1">
                           <select
                             value={selectedResource?.id || ''}
                             onChange={(e) => {
                               const resource = resources.find(r => r.id === e.target.value);
                               if (resource) handleResourceSelect(resource);
                             }}
                             className="w-full px-2 sm:px-3 py-2 pr-6 sm:pr-8 text-xs sm:text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-300 transition-all duration-200 appearance-none"
                           >
                             <option value="">All Resources</option>
                             {resources.map(resource => (
                               <option key={resource.id} value={resource.id}>
                                 {resource.first_name} {resource.last_name}
                               </option>
                             ))}
                           </select>
                           <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:pr-3 pointer-events-none">
                             <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                           </div>
                         </div>

                         {/* Manual Entry Button - Plus icon only on mobile */}
                         <button
                           onClick={() => setIsManualEntryModalOpen(true)}
                           className="bg-blue-600 rounded-lg p-2 sm:px-4 sm:py-2 shadow-sm transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                         >
                           <div className="flex items-center gap-2">
                             <Plus className="h-4 w-4 text-white" />
                             <span className="text-sm font-semibold text-white hidden sm:inline">Manual Entry</span>
                           </div>
                         </button>

                         {/* Date Range Picker */}
                         <div className="relative flex-shrink-0" ref={datePickerRef}>
                         <button
                           type="button"
                           onClick={() => setIsDateRangeOpen(!isDateRangeOpen)}
                           className="px-2 sm:px-3 py-2 text-xs sm:text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-300 transition-all duration-200"
                         >
                           <div className="flex items-center gap-1 sm:gap-2">
                             <span className="text-xs sm:text-sm font-medium text-gray-700">{selectedRange.label}</span>
                             <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 text-gray-400 transition-transform duration-200 ${isDateRangeOpen ? 'rotate-180' : ''}`} />
                           </div>
                         </button>

                        {isDateRangeOpen && (
                          <div className="absolute right-0 top-full mt-2 w-72 bg-white/90 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl py-2 z-50 animate-in slide-in-from-top-2">
                            {DATE_RANGES.map((range) => (
                              <button
                                key={range.value}
                                onClick={() => {
                                  if (range.value === 'custom') {
                                    setIsCustomRangeOpen(true);
                                  } else {
                                    setSelectedRange(range);
                                    setIsDateRangeOpen(false);
                                    loadPayrollHistory();
                                  }
                                }}
                                className={`w-full text-left px-4 py-3 text-sm transition-colors duration-150 ${
                                  selectedRange.value === range.value
                                    ? 'bg-blue-50 text-blue-700 font-medium'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {range.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* History Content */}
                  <div className="min-h-[400px]">
                    {isLoadingHistory ? (
                      <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                          <div className="relative">
                            <div className="w-12 h-12 mx-auto mb-4">
                              <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
                              <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>
                            </div>
                          </div>
                          <p className="text-gray-600">Loading time entries...</p>
                        </div>
                      </div>
                    ) : error ? (
                      <div className="p-8">
                        <div className="max-w-md mx-auto text-center">
                          <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                            <X className="h-8 w-8 text-red-600" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading History</h3>
                          <p className="text-gray-600 mb-4">{error}</p>
                          <button 
                            onClick={loadPayrollHistory}
                            className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-xl hover:bg-blue-200 transition-colors duration-150"
                          >
                            Try Again
                          </button>
                        </div>
                      </div>
                    ) : payrollHistory.length === 0 ? (
                      <div className="flex items-center justify-center h-64">
                        <div className="text-center max-w-md">
                          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center">
                            <Clock className="h-10 w-10 text-blue-400" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-3">No Time Entries</h3>
                          <p className="text-gray-600 leading-relaxed">
                            No time clock entries found for the selected period. 
                            Clock in/out records and manual entries will appear here.
                          </p>
                        </div>
                      </div>
                    ) : (
                       <div className="space-y-3 p-4 pb-6">
                        {payrollHistory.map((entry, index) => (
                            <div
                              key={entry.id}
                              className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
                              style={{
                                animationDelay: `${index * 50}ms`
                              }}
                              onClick={() => {
                                handleEditClick(entry);
                              }}
                            >
                            <div className="p-4">
                              {/* Header */}
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold text-white">
                                    {entry.resources.first_name?.[0]}{entry.resources.last_name?.[0]}
                                  </div>
                                  <div>
                                    <h3 className="text-sm font-semibold text-gray-900">
                                      {entry.resources.first_name} {entry.resources.last_name}
                                    </h3>
                                    <p className="text-xs text-gray-500">{formatDate(entry.clock_in)}</p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(entry);
                                    }}
                                    className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-150"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>

                              {/* Time Details */}
                              <div className="mb-3">
                                <div className="flex items-center space-x-4 text-xs">
                                  {/* Clock In */}
                                  <div className="flex items-center space-x-1">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    <span className="text-gray-600">In: {formatTime(entry.clock_in)}</span>
                                  </div>
                                  
                                  {/* Break */}
                                  {entry.break_start && (
                                    <div className="flex items-center space-x-1">
                                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                      <span className="text-gray-600">
                                        Break: {formatTime(entry.break_start)}
                                        {entry.break_end && ` - ${formatTime(entry.break_end)}`}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {/* Clock Out */}
                                  <div className="flex items-center space-x-1">
                                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                    <span className="text-gray-600">
                                      Out: {entry.clock_out ? formatTime(entry.clock_out) : 'Active'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Duration and Earnings */}
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <div className="text-xs text-gray-500 mb-1">Duration</div>
                                  <div className="text-sm font-bold text-gray-900">
                                    {formatDuration(entry.total_minutes)}
                                  </div>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <div className="text-xs text-gray-500 mb-1">Earnings</div>
                                  <div className="text-sm font-bold text-gray-900">
                                    {formatCurrency(entry.total_earnings)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals with updated styling */}
        {/* Edit Modal */}
        {isEditModalOpen && editingEntry && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Edit Time Entry</h3>
                    <p className="text-sm text-gray-600 mt-1">Modify clock times and rates</p>
                  </div>
                  <button
                    onClick={() => setIsEditModalOpen(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors duration-150"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-4 sm:p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Clock In</label>
                  <input
                    type="datetime-local"
                    name="clockIn"
                    value={editFormData.clockIn}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Clock Out</label>
                  <input
                    type="datetime-local"
                    name="clockOut"
                    value={editFormData.clockOut}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Break Start</label>
                  <input
                    type="datetime-local"
                    name="breakStart"
                    value={editFormData.breakStart}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Break End</label>
                  <input
                    type="datetime-local"
                    name="breakEnd"
                    value={editFormData.breakEnd}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hourly Rate</label>
                  <input
                    type="number"
                    name="rate"
                    value={editFormData.rate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                    step="0.01"
                  />
                </div>
              </div>
              
              <div className="px-4 sm:px-6 py-4 sm:py-6 border-t border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl hover:bg-white/80 transition-all duration-150 w-full sm:w-auto"
                    onClick={() => setIsEditModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-150 shadow-lg hover:shadow-xl w-full sm:w-auto"
                    onClick={handleSaveEdit}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && entryToDelete && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Confirm Delete</h3>
                    <p className="text-sm text-gray-600 mt-1">This action cannot be undone</p>
                  </div>
                  <button
                    onClick={() => setIsDeleteModalOpen(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors duration-150"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-4 sm:p-6">
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete this time entry from {formatDateTime(entryToDelete.clock_in)}?
                  This action cannot be undone.
                </p>
              </div>
              
              <div className="px-4 sm:px-6 py-4 sm:py-6 border-t border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl hover:bg-white/80 transition-all duration-150 w-full sm:w-auto"
                    onClick={() => setIsDeleteModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-red-700 rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-150 shadow-lg hover:shadow-xl w-full sm:w-auto"
                    onClick={handleDeleteConfirm}
                  >
                    Delete Entry
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Manual Entry Modal */}
        <ManualEntryModal
          isOpen={isManualEntryModalOpen}
          onClose={() => setIsManualEntryModalOpen(false)}
          resources={resources}
          onEntryCreated={() => {
            loadPayrollHistory();
            setIsManualEntryModalOpen(false);
          }}
        />

        {/* Active Timer Warning Modal */}
        <ActiveTimerWarningModal
          isOpen={isActiveTimerWarningOpen}
          onClose={() => setIsActiveTimerWarningOpen(false)}
          bookingTitle={activeTimerBookingTitle}
        />
      </div>
    </PermissionGuard>
  );
} 